/**
 * Plan glyph creation and ccmp membership from composition recipes.
 * Recipes run only while adding glyphs. Converting glyphs that already
 * exist reads their components and feature rules, even when those differ
 * from the recipe.
 */
import { parseConnName, connectionTarget } from './conn-names';
const CCMP_GENERATOR = 'space.counterpunch.ccmp';
export function compositionOutputSetting(value) {
    if (value === 'ccmp' || value === 'none') {
        return value;
    }
    return 'materialized';
}
/** Replace each component with `{name}.{position}` when that glyph exists. */
export function expandFormSequence(components, position, hasGlyph) {
    return components.map((name) => {
        const form = `${name}.${position}`;
        return hasGlyph(form) ? form : name;
    });
}
export async function planGlyphAdditions(records, output, font, provider, managedInputs) {
    const plan = await planRecords(records, output, font, provider, managedInputs, false);
    if (output !== 'none') {
        expandConnectionGlyphs(records, output, font, plan);
    }
    return plan;
}
/** Convert glyphs that already have components, without consulting a recipe. */
export function planExistingConversion(items, output) {
    const plan = {
        create: [],
        composites: [],
        clearShells: [],
        ccmpAdd: [],
        ccmpRemove: [],
        ccmpComponents: {},
        supportingCount: 0,
        skipped: [],
        deletes: [],
        kept: [],
        arabicAdd: {},
        arabicRemove: []
    };
    for (const item of items) {
        if (!item.components.length) {
            plan.skipped.push(item.name);
            continue;
        }
        const components = [...item.components];
        if (output === 'ccmp') {
            plan.ccmpAdd.push(item.name);
            plan.clearShells.push(item.name);
            plan.ccmpComponents[item.name] = components;
        }
        else {
            plan.composites.push({ name: item.name, components });
            plan.ccmpRemove.push(item.name);
        }
    }
    return plan;
}
export async function planRebuild(records, output, font, provider, managedInputs) {
    return planRecords(records, output, font, provider, managedInputs, true);
}
async function planRecords(records, output, font, provider, managed, clearExistingShells) {
    const plan = {
        create: [],
        composites: [],
        clearShells: [],
        ccmpAdd: [],
        ccmpRemove: [],
        ccmpComponents: {},
        supportingCount: 0,
        skipped: [],
        deletes: [],
        kept: [],
        arabicAdd: {},
        arabicRemove: []
    };
    const creating = new Set();
    const anchorRequests = [];
    function ensureGlyph(record, requested) {
        if (font.hasGlyph(record.glyph_name) ||
            creating.has(record.glyph_name)) {
            return;
        }
        creating.add(record.glyph_name);
        const category = record.category ||
            (record.general_category.startsWith('M') ? 'Mark' : 'Base');
        plan.create.push({
            name: record.glyph_name,
            codepoints: record.codepoint == null ? [] : [record.codepoint],
            category,
            anchors: [],
            width: category === 'Mark' ? 0 : undefined
        });
        if (!requested) {
            plan.supportingCount += 1;
        }
    }
    if (output === 'none') {
        for (const record of records) {
            if (!font.hasGlyph(record.glyph_name)) {
                ensureGlyph(record, true);
            }
        }
        return plan;
    }
    const resolved = new Map();
    function remember(token, name) {
        resolved.set(token, name);
        return name;
    }
    async function ensureToken(token, requested) {
        const known = resolved.get(token);
        if (known) {
            return known;
        }
        const suffix = token.endsWith('.init')
            ? '.init'
            : token.endsWith('.medi')
                ? '.medi'
                : token.endsWith('.fina')
                    ? '.fina'
                    : '';
        if (token.startsWith('uni')) {
            const root = token.slice(0, token.indexOf('.') > 0 ? token.indexOf('.') : token.length);
            const codepoint = Number.parseInt(root.slice(3), 16);
            if (suffix) {
                const rootName = await ensureToken(root, false);
                if (!rootName) {
                    return null;
                }
                const name = rootName + suffix;
                if (!font.hasGlyph(name)) {
                    ensureGlyph({
                        glyph_name: name,
                        general_category: 'Lo',
                        category: 'Base'
                    }, requested);
                    const anchorNames = (await provider.anchorsFor?.(token)) || [];
                    queueAnchors(anchorRequests, font, name, anchorNames, 'Lo');
                }
                return remember(token, name);
            }
            const name = font.glyphNameForCodepoint(codepoint) ||
                provider.glyphNameForCodepoint(codepoint);
            if (!name) {
                return null;
            }
            remember(token, name);
            const category = provider.categoryForCodepoint(codepoint);
            ensureGlyph({ codepoint, glyph_name: name, general_category: category }, requested);
            if (!font.hasGlyph(name)) {
                const anchorNames = (await provider.anchorsFor?.(token)) ||
                    (await provider.anchors(codepoint));
                queueAnchors(anchorRequests, font, name, anchorNames, category);
            }
            const rootRecipe = await provider.recipe(codepoint);
            if (rootRecipe?.positions) {
                for (const sequence of Object.values(rootRecipe.positions)) {
                    if (sequence.length === 1 && sequence[0] !== token) {
                        await ensureToken(sequence[0], false);
                    }
                }
            }
            return name;
        }
        remember(token, token);
        const entry = await provider.recipeForName?.(token);
        const category = entry?.category || 'Mark';
        ensureGlyph({
            glyph_name: token,
            general_category: category === 'Mark' ? 'Mn' : 'Lo',
            category
        }, requested);
        if (!font.hasGlyph(token)) {
            const anchorNames = (await provider.anchorsFor?.(token)) || [];
            queueAnchors(anchorRequests, font, token, anchorNames, category === 'Mark' ? 'Mn' : 'Lo');
        }
        for (const sequence of Object.values(entry?.positions || {})) {
            for (const nested of sequence) {
                await ensureToken(nested, false);
            }
        }
        if (entry?.components) {
            for (const nested of entry.components) {
                await ensureToken(nested, false);
            }
        }
        return token;
    }
    for (const record of records) {
        if (record.codepoint == null) {
            const name = await ensureToken(record.glyph_name, true);
            const entry = await provider.recipeForName?.(record.glyph_name);
            if (name && entry?.components?.length) {
                const components = [];
                for (const token of entry.components) {
                    const component = await ensureToken(token, false);
                    if (!component) {
                        components.length = 0;
                        break;
                    }
                    components.push(component);
                }
                if (components.length) {
                    plan.composites.push({ name, components });
                }
            }
            continue;
        }
        const recipe = await provider.recipe(record.codepoint);
        if (recipe?.positions) {
            const letter = await ensureToken(uniToken(record.codepoint), true);
            if (!letter) {
                plan.skipped.push(record.glyph_name);
                continue;
            }
            const positions = {};
            let failed = false;
            for (const [position, sequence] of Object.entries(recipe.positions)) {
                const names = [];
                for (const token of sequence) {
                    const resolvedName = await ensureToken(token, false);
                    if (!resolvedName) {
                        failed = true;
                        break;
                    }
                    names.push(resolvedName);
                }
                if (failed) {
                    break;
                }
                positions[position] = names;
            }
            if (failed) {
                plan.skipped.push(record.glyph_name);
                continue;
            }
            const isol = positions.isol || [letter];
            if (output === 'ccmp') {
                if (recipe.decompose?.length) {
                    const decomposed = [];
                    for (const token of recipe.decompose) {
                        const name = await ensureToken(token, false);
                        if (name) {
                            decomposed.push(name);
                        }
                    }
                    plan.ccmpAdd.push(letter);
                    plan.ccmpComponents[letter] = decomposed;
                    if (clearExistingShells && font.hasGlyph(letter)) {
                        plan.clearShells.push(letter);
                    }
                }
                else if (isol.join(' ') !== letter) {
                    plan.arabicAdd[letter] = positions;
                    if (clearExistingShells && font.hasGlyph(letter)) {
                        plan.clearShells.push(letter);
                    }
                }
            }
            else if (isol.join(' ') !== letter) {
                plan.composites.push({ name: letter, components: isol });
                for (const [position, names] of Object.entries(positions)) {
                    if (position === 'isol' || names.length < 2) {
                        continue;
                    }
                    const positional = `${letter}.${position}`;
                    ensureGlyph({
                        glyph_name: positional,
                        general_category: 'Lo',
                        category: 'Base'
                    }, false);
                    plan.composites.push({
                        name: positional,
                        components: names
                    });
                }
                if (managed.includes(letter)) {
                    plan.ccmpRemove.push(letter);
                    plan.arabicRemove.push(letter);
                }
            }
            continue;
        }
        if (!recipe) {
            if (!font.hasGlyph(record.glyph_name)) {
                ensureGlyph(record, true);
            }
            plan.skipped.push(record.glyph_name);
            continue;
        }
        const componentNames = [];
        for (const component of recipe.components || []) {
            if (component.codepoint == null) {
                plan.skipped.push(record.glyph_name);
                componentNames.length = 0;
                break;
            }
            const codepoint = component.codepoint;
            const name = font.glyphNameForCodepoint(codepoint) ||
                provider.glyphNameForCodepoint(codepoint);
            if (!name) {
                plan.skipped.push(record.glyph_name);
                componentNames.length = 0;
                break;
            }
            componentNames.push(name);
            const componentRecord = {
                codepoint,
                glyph_name: name,
                general_category: provider.categoryForCodepoint(codepoint)
            };
            ensureGlyph(componentRecord, false);
            if (!font.hasGlyph(name)) {
                const names = await provider.anchors(codepoint);
                queueAnchors(anchorRequests, font, name, names, componentRecord.general_category);
            }
        }
        if (!componentNames.length) {
            continue;
        }
        ensureGlyph(record, true);
        const managedNow = managed.includes(record.glyph_name);
        if (output === 'ccmp') {
            plan.ccmpAdd.push(record.glyph_name);
            plan.ccmpComponents[record.glyph_name] = componentNames;
            if (clearExistingShells && font.hasGlyph(record.glyph_name)) {
                plan.clearShells.push(record.glyph_name);
            }
        }
        else {
            plan.composites.push({
                name: record.glyph_name,
                components: componentNames
            });
            if (managedNow) {
                plan.ccmpRemove.push(record.glyph_name);
            }
        }
    }
    if (provider.drawings && plan.create.length) {
        const drawings = await provider.drawings(plan.create.map((glyph) => glyph.name));
        for (const glyph of plan.create) {
            const drawing = drawings[glyph.name];
            if (!drawing) {
                continue;
            }
            glyph.drawing = drawing;
            glyph.anchors = [];
        }
    }
    if (anchorRequests.length) {
        const positioned = await provider.anchorPositions(anchorRequests);
        for (const row of positioned) {
            const glyph = plan.create.find((item) => item.name === row.glyph_name);
            if (!glyph || glyph.drawing) {
                continue;
            }
            for (const [name, point] of Object.entries(row.positions)) {
                glyph.anchors.push({
                    masterId: row.master_id,
                    name,
                    x: point[0],
                    y: point[1]
                });
            }
        }
    }
    return plan;
}
function queueAnchors(requests, font, glyphName, names, category) {
    if (!names.length) {
        return;
    }
    const role = category.startsWith('M')
        ? 'mark'
        : category === 'Ll'
            ? 'lower'
            : 'upper';
    const attaching = names.find((name) => name.startsWith('_'));
    const band = role !== 'mark'
        ? null
        : attaching === '_bottom' || attaching === '_ogonek'
            ? 'below'
            : attaching === '_center'
                ? 'overlay'
                : 'above';
    for (const master of font.masters) {
        requests.push({
            glyph_name: glyphName,
            master_id: master.id,
            names,
            role,
            band,
            metrics: master.metrics,
            width: 500,
            bbox: null
        });
    }
}
function expandConnectionGlyphs(records, output, font, plan) {
    if (!font.glyphNames || !font.composition) {
        return;
    }
    const creating = new Set(plan.create.map((glyph) => glyph.name));
    for (const record of records) {
        const seed = parseConnName(record.glyph_name);
        if (!seed) {
            continue;
        }
        const target = connectionTarget(seed.source, seed.id, seed.step);
        for (const name of font.glyphNames()) {
            if (parseConnName(name) || name === seed.source) {
                continue;
            }
            const sequence = font.composition(name);
            if (!sequence.length || sequence[0] !== seed.source) {
                continue;
            }
            const derived = connectionTarget(name, seed.id, seed.step);
            if (font.hasGlyph(derived) || creating.has(derived)) {
                continue;
            }
            creating.add(derived);
            const components = [target, ...sequence.slice(1)];
            plan.create.push({
                name: derived,
                codepoints: [],
                category: 'Base',
                anchors: []
            });
            plan.supportingCount += 1;
            if (output === 'ccmp') {
                plan.ccmpAdd.push(derived);
                plan.ccmpComponents[derived] = components;
                plan.clearShells.push(derived);
            }
            else {
                plan.composites.push({ name: derived, components });
            }
        }
    }
}
export function planArabicConversion(families, output) {
    const plan = {
        create: [],
        composites: [],
        clearShells: [],
        ccmpAdd: [],
        ccmpRemove: [],
        ccmpComponents: {},
        supportingCount: 0,
        skipped: [],
        deletes: [],
        kept: [],
        arabicAdd: {},
        arabicRemove: []
    };
    for (const family of families) {
        if (family.missing) {
            plan.skipped.push(family.name);
            continue;
        }
        const isol = family.sequences.isol || [];
        if (output === 'ccmp') {
            if (family.chain && isol.length) {
                plan.ccmpAdd.push(family.name);
                plan.ccmpComponents[family.name] = isol;
            }
            else {
                plan.arabicAdd[family.name] = family.sequences;
            }
            plan.clearShells.push(family.name);
            for (const positional of formMembers(family)) {
                if (family.pinned.includes(positional)) {
                    plan.kept.push(positional);
                }
                else {
                    plan.deletes.push(positional);
                }
            }
        }
        else {
            if (isol.length && isol.join(' ') !== family.name) {
                plan.composites.push({ name: family.name, components: isol });
            }
            for (const [position, sequence] of Object.entries(family.sequences)) {
                if (position === 'isol' ||
                    !sequence.length ||
                    (sequence.length === 1 && sequence[0] === family.name)) {
                    continue;
                }
                const positional = `${family.name}.${position}`;
                plan.create.push({
                    name: positional,
                    codepoints: [],
                    category: 'Base',
                    anchors: []
                });
                plan.composites.push({
                    name: positional,
                    components: sequence
                });
            }
            plan.ccmpRemove.push(family.name);
            plan.arabicRemove.push(family.name);
        }
        for (const conn of family.conn || []) {
            if (!conn.components.length) {
                continue;
            }
            if (output === 'ccmp') {
                plan.ccmpAdd.push(conn.name);
                plan.ccmpComponents[conn.name] = conn.components;
                plan.clearShells.push(conn.name);
            }
            else {
                plan.composites.push({
                    name: conn.name,
                    components: conn.components
                });
                plan.ccmpRemove.push(conn.name);
            }
        }
    }
    return plan;
}
function formMembers(family) {
    if (family.members) {
        return family.members;
    }
    return ['init', 'medi', 'fina']
        .filter((position) => (family.sequences[position] || []).length > 0)
        .map((position) => `${family.name}.${position}`);
}
export const CCMP_PLUGIN_ID = CCMP_GENERATOR;
function uniToken(codepoint) {
    return `uni${codepoint.toString(16).toUpperCase().padStart(4, '0')}`;
}
